import React, { useState, useRef, useEffect } from 'react';
import NepaliDate from 'nepali-date';
import {
  BS_MONTHS_FULL, bsDaysInMonth, bsToAD, fmtAD, getBSYearOptions,
  getCurrentBSYear, getCurrentBSMonth, getCurrentBSDay,
} from '../utils/bs';

// ─── NepaliDatePicker ─────────────────────────────────────────────────────────

interface NepaliDatePickerProps {
  value: string;        // 'yyyy-MM-dd' AD, or ''
  onChange: (adDateStr: string) => void;
  className?: string;
  placeholder?: string;
  maxDate?: string;      // 'yyyy-MM-dd' AD — disables any BS date after this
}

export function NepaliDatePicker({
  value,
  onChange,
  className = '',
  placeholder = 'Select BS date',
  maxDate,
}: NepaliDatePickerProps) {
  const todayBSYear  = getCurrentBSYear();
  const todayBSMonth = getCurrentBSMonth();
  const todayBSDay   = getCurrentBSDay();

  function parseToBS(adStr: string): NepaliDate | null {
    if (!adStr) return null;
    try {
      const parts = adStr.slice(0, 10).split('-').map(Number);
      if (parts.length !== 3 || parts.some(isNaN)) return null;
      // Use local noon to avoid Nepal's historical timezone offset issue (UTC+5:30 → UTC+5:45 in 1986)
      const noon = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
      if (isNaN(noon.getTime())) return null;
      return new NepaliDate(noon);
    } catch { return null; }
  }

  const selectedBS   = parseToBS(value);
  const maxBS        = maxDate ? parseToBS(maxDate) : null;
  const [open, setOpen]         = useState(false);
  const [viewYear,  setViewYear]  = useState(selectedBS?.getYear()  ?? todayBSYear);
  const [viewMonth, setViewMonth] = useState(selectedBS?.getMonth() ?? todayBSMonth);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nd = parseToBS(value);
    if (nd) { setViewYear(nd.getYear()); setViewMonth(nd.getMonth()); }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  const atMaxMonth = maxBS != null && viewYear === maxBS.getYear() && viewMonth === maxBS.getMonth();

  function isDayDisabled(day: number): boolean {
    if (!maxBS) return false;
    if (viewYear !== maxBS.getYear() || viewMonth !== maxBS.getMonth()) {
      return viewYear > maxBS.getYear() || (viewYear === maxBS.getYear() && viewMonth > maxBS.getMonth());
    }
    return day > maxBS.getDate();
  }

  function selectDay(day: number) {
    if (isDayDisabled(day)) return;
    onChange(fmtAD(bsToAD(viewYear, viewMonth, day)));
    setOpen(false);
  }

  function goToday() {
    setViewYear(todayBSYear);
    setViewMonth(todayBSMonth);
    onChange(fmtAD(bsToAD(todayBSYear, todayBSMonth, todayBSDay)));
    setOpen(false);
  }

  const daysInMonth  = bsDaysInMonth(viewYear, viewMonth);
  const firstWeekday = bsToAD(viewYear, viewMonth, 1).getDay();

  const grid: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(d);
  while (grid.length % 7 !== 0) grid.push(null);

  const displayValue = selectedBS
    ? `${selectedBS.getDate()} ${BS_MONTHS_FULL[selectedBS.getMonth()]} ${selectedBS.getYear()}`
    : '';

  const yearOptions = maxBS
    ? getBSYearOptions(20).filter(y => y <= maxBS.getYear())
    : getBSYearOptions(20);

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        readOnly
        value={displayValue}
        placeholder={placeholder}
        onClick={() => setOpen(o => !o)}
        className={`input cursor-pointer ${className}`}
      />
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded-xl shadow-2xl border border-hydro-border p-3 w-72">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={prevMonth} className="p-1 rounded hover:bg-slate-100 text-slate-600 font-bold text-base leading-none">‹</button>
            <div className="flex items-center gap-1.5">
              <select
                value={viewMonth}
                onChange={e => setViewMonth(Number(e.target.value))}
                className="input text-xs py-0.5 px-1.5"
              >
                {BS_MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <select
                value={viewYear}
                onChange={e => setViewYear(Number(e.target.value))}
                className="input text-xs py-0.5 px-1.5 w-[4.5rem]"
              >
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button
              onClick={nextMonth}
              disabled={atMaxMonth}
              className="p-1 rounded hover:bg-slate-100 text-slate-600 font-bold text-base leading-none disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              ›
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} className="text-center text-xs text-slate-400 font-medium py-0.5">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {grid.map((day, i) => {
              if (!day) return <div key={i} />;
              const isToday    = viewYear === todayBSYear && viewMonth === todayBSMonth && day === todayBSDay;
              const isSelected = selectedBS
                && viewYear  === selectedBS.getYear()
                && viewMonth === selectedBS.getMonth()
                && day       === selectedBS.getDate();
              const disabled = isDayDisabled(day);
              return (
                <button
                  key={i}
                  onClick={() => selectDay(day)}
                  disabled={disabled}
                  className={`text-xs py-1.5 rounded-md font-medium transition-colors ${
                    disabled
                      ? 'text-slate-300 cursor-not-allowed'
                      : isSelected
                      ? 'bg-brand-600 text-white'
                      : isToday
                      ? 'bg-brand-100 text-brand-700 font-bold'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-2 pt-2 border-t border-hydro-border flex items-center justify-between">
            <button onClick={goToday} className="text-xs text-brand-600 hover:text-brand-800 font-medium">Today</button>
            {value && (
              <button onClick={() => { onChange(''); setOpen(false); }} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── NepaliDateTimePicker ─────────────────────────────────────────────────────

interface NepaliDateTimePickerProps {
  value: string;   // 'yyyy-MM-ddTHH:mm' or ''
  onChange: (isoStr: string) => void;
  className?: string;
}

export function NepaliDateTimePicker({
  value,
  onChange,
  className = '',
}: NepaliDateTimePickerProps) {
  const datePart = value ? value.slice(0, 10) : '';
  const now      = new Date();
  const defaultTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const timePart = (value && value.length >= 16) ? value.slice(11, 16) : defaultTime;

  function handleDateChange(adStr: string) {
    if (!adStr) { onChange(''); return; }
    onChange(`${adStr}T${timePart}`);
  }

  function handleTimeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const t = e.target.value;
    if (datePart) onChange(`${datePart}T${t}`);
  }

  return (
    <div className={`flex gap-2 ${className}`}>
      <NepaliDatePicker value={datePart} onChange={handleDateChange} className="flex-1" />
      <input type="time" value={timePart} onChange={handleTimeChange} className="input w-28" />
    </div>
  );
}
